import {z} from 'zod';

import {BigDecimalSchema, UuidResponseBodySchema} from '@dms/api/shared';

// Enums
export const PurchasePriceMarginTypeSchema = z.enum(['FIXED_AMOUNT', 'PERCENTAGE']);
export type PurchasePriceMarginType = z.infer<typeof PurchasePriceMarginTypeSchema>;

// Rule building blocks
export const PurchasePriceBandSchema = z.object({
  upperBoundInclusive: BigDecimalSchema.nullable(),
  margin: z.object({
    type: PurchasePriceMarginTypeSchema,
    value: BigDecimalSchema,
  }),
});
export type PurchasePriceBand = z.infer<typeof PurchasePriceBandSchema>;

export const PurchasePriceRuleScopeSchema = z.object({
  makes: z.array(z.string()),
  modelFamilies: z.array(z.string()),
  trims: z.array(z.string()),
});
export type PurchasePriceRuleScope = z.infer<typeof PurchasePriceRuleScopeSchema>;

export const PurchasePriceModifierSchema = z.object({
  enabled: z.boolean(),
  percentage: BigDecimalSchema,
});
export type PurchasePriceModifier = z.infer<typeof PurchasePriceModifierSchema>;

export const PurchasePriceThresholdModifierSchema = PurchasePriceModifierSchema.extend({
  threshold: z.number().int().nullable(),
});
export type PurchasePriceThresholdModifier = z.infer<typeof PurchasePriceThresholdModifierSchema>;

export const PurchasePriceRuleModifiersSchema = z.object({
  electricVehicle: PurchasePriceModifierSchema,
  highMileage: PurchasePriceThresholdModifierSchema,
  higherAge: PurchasePriceThresholdModifierSchema,
});
export type PurchasePriceRuleModifiers = z.infer<typeof PurchasePriceRuleModifiersSchema>;

// Request / response bodies
export const PurchasePriceRuleRequestBodySchema = z.object({
  name: z.string(),
  scope: PurchasePriceRuleScopeSchema.nullable(),
  priceBands: z.array(PurchasePriceBandSchema),
  warrantyCost: BigDecimalSchema,
  modifiers: PurchasePriceRuleModifiersSchema,
});
export type PurchasePriceRuleRequestBody = z.infer<typeof PurchasePriceRuleRequestBodySchema>;

export const PurchasePriceRuleResponseBodySchema = PurchasePriceRuleRequestBodySchema.extend({
  id: z.string().uuid(),
  position: z.number().int(),
  system: z.boolean(),
  shadowedByRuleId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchasePriceRuleResponseBody = z.infer<typeof PurchasePriceRuleResponseBodySchema>;

const PurchasePriceRuleIdArgSchema = z.object({
  id: z.string().uuid(),
});

// listPurchasePriceRules
export const ListPurchasePriceRulesApiResponseSchema = z.array(PurchasePriceRuleResponseBodySchema);
export type ListPurchasePriceRulesApiResponse = z.infer<
  typeof ListPurchasePriceRulesApiResponseSchema
>;

// getPurchasePriceRule
export const GetPurchasePriceRuleApiArgSchema = PurchasePriceRuleIdArgSchema;
export type GetPurchasePriceRuleApiArg = z.infer<typeof GetPurchasePriceRuleApiArgSchema>;

export const GetPurchasePriceRuleApiResponseSchema = PurchasePriceRuleResponseBodySchema;
export type GetPurchasePriceRuleApiResponse = PurchasePriceRuleResponseBody;

// createPurchasePriceRule
export const CreatePurchasePriceRuleApiArgSchema = z.object({
  purchasePriceRuleRequestBody: PurchasePriceRuleRequestBodySchema,
});
export type CreatePurchasePriceRuleApiArg = z.infer<typeof CreatePurchasePriceRuleApiArgSchema>;

export const CreatePurchasePriceRuleApiResponseSchema = UuidResponseBodySchema;
export type CreatePurchasePriceRuleApiResponse = z.infer<
  typeof CreatePurchasePriceRuleApiResponseSchema
>;

// updatePurchasePriceRule
export const UpdatePurchasePriceRuleApiArgSchema = PurchasePriceRuleIdArgSchema.extend({
  purchasePriceRuleRequestBody: PurchasePriceRuleRequestBodySchema,
});
export type UpdatePurchasePriceRuleApiArg = z.infer<typeof UpdatePurchasePriceRuleApiArgSchema>;

export const UpdatePurchasePriceRuleApiResponseSchema = z.null();
export type UpdatePurchasePriceRuleApiResponse = /** status 204 */ z.infer<
  typeof UpdatePurchasePriceRuleApiResponseSchema
>;

// deletePurchasePriceRule
export const DeletePurchasePriceRuleApiArgSchema = PurchasePriceRuleIdArgSchema;
export type DeletePurchasePriceRuleApiArg = z.infer<typeof DeletePurchasePriceRuleApiArgSchema>;

export const DeletePurchasePriceRuleApiResponseSchema = z.null();
export type DeletePurchasePriceRuleApiResponse = /** status 204 */ z.infer<
  typeof DeletePurchasePriceRuleApiResponseSchema
>;

// reorderPurchasePriceRules
export const PurchasePriceRulePositionRequestBodySchema = z.object({
  id: z.string().uuid(),
  position: z.number().int().min(0),
});
export type PurchasePriceRulePositionRequestBody = z.infer<
  typeof PurchasePriceRulePositionRequestBodySchema
>;

export const ReorderPurchasePriceRulesRequestBodySchema = z.object({
  items: z.array(PurchasePriceRulePositionRequestBodySchema),
});
export type ReorderPurchasePriceRulesRequestBody = z.infer<
  typeof ReorderPurchasePriceRulesRequestBodySchema
>;

export const ReorderPurchasePriceRulesApiArgSchema = z.object({
  reorderPurchasePriceRulesRequestBody: ReorderPurchasePriceRulesRequestBodySchema,
});
export type ReorderPurchasePriceRulesApiArg = z.infer<typeof ReorderPurchasePriceRulesApiArgSchema>;

export const ReorderPurchasePriceRulesApiResponseSchema = z.null();
export type ReorderPurchasePriceRulesApiResponse = /** status 204 */ z.infer<
  typeof ReorderPurchasePriceRulesApiResponseSchema
>;
